// queries/useEnums.ts
import { API_ENDPOINTS } from '@/api/endpoints';
import { useAuthStatus } from '@/auth/useAuthStatus';
import { Area } from '@/stores/SubprojectStore';
import { apiFetch } from '@/util/apiClient';
import { useQuery } from '@tanstack/react-query';

export interface CSIEnum {
  name: string;
  value: string;
  description?: string;
}

export interface CSIBuildingUsage {
  name: string;
  value: string;
  description: string[];
}

export interface AreaEnum {
  name: string;
  symbol: string;
  unitPerM2: number;
}

export interface EnergySource {
  name: string;
  value: string;
  unit: string;
  unitPerMWh: number;
  isRenewable: boolean;
}

export function useEnums() {
  const { isAuthenticated } = useAuthStatus();

  const { data } = useQuery({
    queryKey: ['enums'],
    queryFn: async () => {
      const [
        sectors,
        technologyCategoriesAll,
        technologyCategoriesBuildings,
        buildingUsages,
        areaUnits,
        energySources,
      ] = await Promise.all([
        // TODO: Fetch constants using a single request?
        apiFetch<CSIEnum[]>(API_ENDPOINTS.CONSTANTS.sectors),
        apiFetch<CSIEnum[]>(API_ENDPOINTS.CONSTANTS.technologyCategories.all),
        apiFetch<CSIBuildingUsage[]>(
          API_ENDPOINTS.CONSTANTS.technologyCategories.buildings,
        ),
        apiFetch<CSIEnum[]>(API_ENDPOINTS.CONSTANTS.buildingUsages),
        apiFetch<AreaEnum[]>(API_ENDPOINTS.CONSTANTS.areaUnits),
        apiFetch<EnergySource[]>(API_ENDPOINTS.CONSTANTS.energySources),
      ]);

      return {
        sectors,
        technologyCategoriesAll,
        technologyCategoriesBuildings,
        buildingUsages,
        areaUnits,
        energySources,
      };
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    sectors: data?.sectors ?? [],
    technologyCategoriesAll: data?.technologyCategoriesAll ?? [],
    technologyCategoriesBuildings: data?.technologyCategoriesBuildings ?? [],
    buildingUsages: data?.buildingUsages ?? [],
    areaUnits: data?.areaUnits ?? [],
    energySources: data?.energySources ?? [],
  };
}

// Helper function to convert area between units
export function convertArea(area: Area, areaUnits: AreaEnum[]): Area[] {
  if (!areaUnits || areaUnits.length === 0) {
    throw new Error('Cannot calculate until area units are loaded');
  }

  const baseUnit = areaUnits.find((au) => au.name === area.unit);
  const goldenUnit = areaUnits.find((au) => au.name === 'SquareMeters');

  if (!baseUnit || !goldenUnit) {
    throw new Error('Unable to find base/golden unit');
  }

  const inSquareMeter = area.value / baseUnit.unitPerM2;
  return areaUnits.map((unit) => ({
    value: inSquareMeter * unit.unitPerM2,
    unit: unit.name || '',
  }));
}
